import { useNavigate } from "react-router-dom";
import { logout } from "@/utils/authentication";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, Languages } from "lucide-react";
import { Cog8ToothIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { User } from "@/graphql/__generated__/graphql";
import { UserAvatar } from "@/components/shared/common";
import { useTranslation } from "react-i18next";

interface PortfolioComponentProps {
  user: User;
  isAdminUser: boolean;
}

function PortfolioComponent({ user, isAdminUser }: PortfolioComponentProps) {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation(["components"]);
  // const { setTheme } = useTheme();

  const currentLanguage = i18n.language;

  const languages = [
    { name: t("header.languages.english"), code: "en", icon: "🇬🇧" },
    { name: t("header.languages.german"), code: "de", icon: "🇩🇪" },
  ];

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    window.dispatchEvent(new Event("languagechange"));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-cy="dropdown-menu-trigger">
        <UserAvatar user={user as User} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mr-4">
        <DropdownMenuLabel>{t("header.profile.title")}</DropdownMenuLabel>
        <DropdownMenuLabel className="font-normal text-slate-600">{`${user?.firstname} ${user?.lastname}`}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* <DropdownMenuSub>
              <DropdownMenuSubTrigger>Set theme</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    Light
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    System
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub> */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Languages className="mr-2 h-4 w-4" />
            {t("header.changelanguage")} {languages?.find((lang) => lang.code === currentLanguage)?.icon}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {
                languages.map((lang) => {
                  return (
                    <DropdownMenuItem className="cursor-pointer" key={lang.code} onClick={() => changeLanguage(lang.code)}>
                      {lang.icon} {lang.name}
                    </DropdownMenuItem>
                  );
                })
              }
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        {!isAdminUser && (
          <>
            <DropdownMenuItem className="cursor-pointer" onClick={() => { navigate("/settings"); }} data-cy="settings-menu-item">
              <Cog8ToothIcon className="mr-2 h-4 w-4" />
              <span>{t("header.profile.settings")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem className="cursor-pointer" onClick={() => window.open(import.meta.env.VITE_HELP_PAGE_URL, "_blank", "noopener,noreferrer")}>
          <QuestionMarkCircleIcon className="mr-2 h-4 w-4" />
          {t("header.profile.help")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={() => { logout(); navigate("/login"); }}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("header.profile.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PortfolioComponent;
